
import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, HelpCircle, Users, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatLocationForDisplay } from "@/utils/locationUtils";

interface Cast {
  id: string;
  iso_request: string;
  created_at: string;
  solved: boolean;
  location_address: string;
}

interface Team {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  created_by: string;
}

interface TrendsSummaryProps {
  challenges?: string[];
  userId?: string;
}

export const TrendsSummary = ({ 
  challenges = [], 
  userId
}: TrendsSummaryProps) => {
  const [unsolvedCasts, setUnsolvedCasts] = useState<Cast[]>([]);
  const [solvedCount, setSolvedCount] = useState(0);
  const [createdTeams, setCreatedTeams] = useState<Team[]>([]);
  const [memberTeams, setMemberTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch casts
        const { data: casts, error: castsError } = await supabase
          .from('casts')
          .select('id, iso_request, created_at, solved, location_address')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (castsError) {
          console.error('Error fetching casts:', castsError);
        } else {
          const unsolved = casts?.filter(cast => !cast.solved) || [];
          const solved = casts?.filter(cast => cast.solved) || [];
          
          setUnsolvedCasts(unsolved);
          setSolvedCount(solved.length);
        }

        // Fetch teams created by the user
        const { data: userCreatedTeams, error: createdTeamsError } = await supabase
          .from('teams')
          .select('id, name, description, created_at, created_by')
          .eq('created_by', userId)
          .order('created_at', { ascending: false });

        if (createdTeamsError) {
          console.error('Error fetching created teams:', createdTeamsError);
        } else {
          setCreatedTeams(userCreatedTeams || []);
        }

        // Fetch teams where user is a member
        const { data: memberTeamsData, error: memberTeamsError } = await supabase
          .from('team_members')
          .select(`
            team_id,
            teams!inner(
              id,
              name,
              description,
              created_at,
              created_by
            )
          `)
          .eq('user_id', userId);

        if (memberTeamsError) {
          console.error('Error fetching member teams:', memberTeamsError);
        } else {
          const memberTeamsList = memberTeamsData?.map(item => item.teams).filter(Boolean) || [];
          setMemberTeams(memberTeamsList as Team[]);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // Mock data for demonstration - this would come from real data
  const mockChallenges = challenges.length > 0 ? challenges : [
    "Pest Control Issues",
    "Weather Adaptation",
    "Market Price Volatility"
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Farm Trends Summary</h4>
        
        {/* Current Challenges */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle size={16} className="text-orange-600" />
            <span className="text-sm font-medium text-gray-700">Current Challenges</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {mockChallenges.slice(0, 3).map((challenge, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full"
              >
                {challenge}
              </span>
            ))}
          </div>
        </div>

        {/* ISO Requests */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <HelpCircle size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-gray-700">ISO Requests</span>
          </div>
          
          <div className="flex items-center space-x-4 mb-3">
            <div className="bg-blue-50 px-3 py-2 rounded-lg">
              <div className="text-lg font-bold text-blue-600">{unsolvedCasts.length}</div>
              <div className="text-xs text-blue-600">Unsolved</div>
            </div>
            <div className="bg-green-50 px-3 py-2 rounded-lg">
              <div className="text-lg font-bold text-green-600">{solvedCount}</div>
              <div className="text-xs text-green-600">Solved</div>
            </div>
          </div>

          {/* List of Unsolved ISO Requests */}
          {loading ? (
            <div className="text-xs text-gray-500">Loading ISO requests...</div>
          ) : unsolvedCasts.length > 0 ? (
            <div className="space-y-2">
              {unsolvedCasts.slice(0, 3).map((cast) => (
                <div key={cast.id} className="bg-blue-50 p-2 rounded border-l-2 border-blue-500">
                  <div className="text-xs text-blue-800 font-medium mb-1 line-clamp-2">
                    {cast.iso_request}
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-blue-600">
                    <Clock size={10} />
                    <span>{formatDate(cast.created_at)}</span>
                    <span>•</span>
                    <span className="truncate">{formatLocationForDisplay(cast.location_address, "Location available")}</span>
                  </div>
                </div>
              ))}
              {unsolvedCasts.length > 3 && (
                <div className="text-xs text-blue-600 text-center py-1">
                  +{unsolvedCasts.length - 3} more unsolved requests
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">No unsolved ISO requests</div>
          )}
        </div>

        {/* tAG Teams Created */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Users size={16} className="text-green-600" />
            <span className="text-sm font-medium text-gray-700">tAG Teams Created</span>
          </div>
          {loading ? (
            <div className="text-xs text-gray-500">Loading created teams...</div>
          ) : createdTeams.length > 0 ? (
            <div className="space-y-2">
              {createdTeams.slice(0, 3).map((team) => (
                <div key={team.id} className="bg-green-50 p-2 rounded border-l-2 border-green-500">
                  <div className="text-xs text-green-800 font-medium mb-1">
                    {team.name}
                  </div>
                  {team.description && (
                    <div className="text-xs text-green-600 line-clamp-1 mb-1">
                      {team.description}
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-xs text-green-600">
                    <Clock size={10} />
                    <span>Created {formatDate(team.created_at)}</span>
                  </div>
                </div>
              ))}
              {createdTeams.length > 3 && (
                <div className="text-xs text-green-600 text-center py-1">
                  +{createdTeams.length - 3} more teams created
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">No teams created yet</div>
          )}
        </div>

        {/* tAG Teams Member Of */}
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <Users size={16} className="text-purple-600" />
            <span className="text-sm font-medium text-gray-700">tAG Teams Member Of</span>
          </div>
          {loading ? (
            <div className="text-xs text-gray-500">Loading member teams...</div>
          ) : memberTeams.length > 0 ? (
            <div className="space-y-2">
              {memberTeams.slice(0, 3).map((team) => (
                <div key={team.id} className="bg-purple-50 p-2 rounded border-l-2 border-purple-500">
                  <div className="text-xs text-purple-800 font-medium mb-1">
                    {team.name}
                  </div>
                  {team.description && (
                    <div className="text-xs text-purple-600 line-clamp-1 mb-1">
                      {team.description}
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-xs text-purple-600">
                    <Clock size={10} />
                    <span>Joined {formatDate(team.created_at)}</span>
                  </div>
                </div>
              ))}
              {memberTeams.length > 3 && (
                <div className="text-xs text-purple-600 text-center py-1">
                  +{memberTeams.length - 3} more teams joined
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">Not a member of any teams yet</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
